import { makeStyles } from "tss-react/mui";
import { actionSecondary } from "@/shared/styles/action.styles";
import { surfaceRaised } from "@/shared/styles/surface.styles";

const styles = makeStyles()((theme) => ({
  root: { display: "flex", flexDirection: "column", alignItems: "center", width: "100%", minWidth: 0 },
  compactRoot: { alignItems: "stretch" },
  compactHeader: {
    "&&": {
      flexDirection: "row",
      justifyContent: "flex-end",
      alignItems: "center",
      width: "100%",
      marginTop: 0,
    },
    "& button": { ...actionSecondary(theme), minHeight: 40, marginTop: 0 },
  },
  compactList: { display: "flex", flexDirection: "column", gap: theme.design.space.md, width: "100%", minWidth: 0, marginTop: theme.design.space.md },
  compactItem: {
    ...surfaceRaised(theme),
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.4fr) minmax(240px, .6fr)",
    gap: theme.design.space.lg,
    alignItems: "center",
    minWidth: 0,
    padding: "20px 24px",
    [theme.breakpoints.down("md")]: { gridTemplateColumns: "minmax(0, 1fr)" },
    [theme.breakpoints.down("sm")]: { padding: "18px" },
  },
  compactMain: { display: "flex", flexDirection: "column", gap: theme.design.space.lg, minWidth: 0 },
  compactDate: {
    color: theme.design.color.text.muted,
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 12,
    fontVariantNumeric: "tabular-nums",
  },
  compactPlayers: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
    alignItems: "center",
    gap: theme.design.space.md,
    minWidth: 0,
    "& > div": { minWidth: 0 },
    "& > div > :last-child": { display: "block", marginTop: 5, fontSize: 18, fontWeight: 700, overflowWrap: "anywhere" },
    [theme.breakpoints.down("sm")]: { gap: theme.design.space.sm, "& > div > :last-child": { fontSize: 15 } },
  },
  compactArrow: { color: theme.design.color.status.danger, fontSize: 20, fontWeight: 700 },
  compactDetails: {
    display: "flex",
    flexDirection: "column",
    gap: theme.design.space.lg,
    minWidth: 0,
    [theme.breakpoints.down("md")]: { display: "grid", gridTemplateColumns: "minmax(0, .7fr) minmax(0, 1.3fr)" },
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "minmax(0, 1fr)" },
  },
  compactLabel: {
    color: theme.design.color.text.muted,
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: ".08em",
    textTransform: "uppercase",
  },
  compactResult: {
    display: "inline-flex",
    alignItems: "center",
    width: "fit-content",
    marginTop: 6,
    padding: "6px 10px",
    border: `1px solid ${theme.design.color.border.default}`,
    borderRadius: theme.design.radius.pill,
    backgroundColor: theme.design.color.surface.control,
  },
  header: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    marginTop: 32,

    [theme.breakpoints.down("sm")]: {
      "& p": {
        fontSize: 24,
      },
    },
  },

  icon: {
    width: 80,
    height: 80,

    [theme.breakpoints.down("sm")]: {
      width: 40,
      height: 40,
    },
  },

  subtitle: {
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 14,
    fontWeight: 600,
    color: "#aaa",
    textAlign: "center",
    maxWidth: 520,
    lineHeight: 1.4,
  },

  refreshButton: {
    borderRadius: 8,
    border: "1px solid #000",
    fontFamily: theme.design.typography.fontFamily,
    fontWeight: 900,
    fontSize: 14,
    textTransform: "none",
    backgroundColor: "rgb(37, 39, 40)",
    maxHeight: 40,
    padding: "6px 24px",
    color: "white",
    marginTop: 8,

    "&:hover": {
      backgroundColor: "rgb(50, 55, 56)",
    },

    "&:disabled": {
      opacity: 0.6,
      color: "white",
    },
  },

  list: {
    display: "flex",
    flexDirection: "column",
    marginTop: 16,
    maxWidth: 1080,
    width: "100%",
  },

  listHeader: {
    display: "grid",
    gridTemplateColumns: "1.3fr 1.3fr 1.3fr 1fr 1.3fr",
    alignItems: "center",
    gap: 8,
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    padding: "8px 16px",
    borderRadius: "4px 4px 0 0",

    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1.1fr 1.1fr 1.1fr 0.9fr 1.1fr",
      gap: 4,
      padding: "8px",
      "& p": {
        fontSize: 11,
      },
    },
  },

  listItem: {
    display: "grid",
    gridTemplateColumns: "1.3fr 1.3fr 1.3fr 1fr 1.3fr",
    alignItems: "center",
    gap: 8,
    padding: "14px 16px",
    borderBottom: "1px solid rgba(255, 255, 255, 0.15)",
    backgroundColor: "rgba(0, 0, 0, 0.2)",

    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1.1fr 1.1fr 1.1fr 0.9fr 1.1fr",
      gap: 4,
      padding: "10px 8px",
      "& p": {
        fontSize: 11,
      },
    },
  },

  headerCell: {
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 16,
    fontWeight: 900,
    color: "white",
  },

  timestamp: {
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 14,
    fontWeight: 700,
    color: "#aaa",
  },

  playerLink: {
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 14,
    fontWeight: 700,
    color: "white",
    textDecoration: "none",

    "&:hover": {
      color: "#ff8a8a",
      textDecoration: "underline",
    },
  },

  playerText: {
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 14,
    fontWeight: 700,
    color: "white",
  },

  playerCrossed: {
    textDecoration: "line-through",
    color: "#888 !important",
    opacity: 0.85,

    "&:hover": {
      color: "#aaa !important",
    },
  },

  resultSingle: {
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 13,
    fontWeight: 700,
    color: "#8fd48f",
  },

  resultDouble: {
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 13,
    fontWeight: 700,
    color: "#f5d76e",
  },

  resultBackfire: {
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 13,
    fontWeight: 700,
    color: "#ff8a8a",
  },

  bulletsCell: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },

  bulletLine: {
    display: "flex",
    alignItems: "center",
    gap: 8,

    [theme.breakpoints.down("sm")]: {
      gap: 4,
    },
  },

  bulletLabel: {
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 10,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "#9aa0a6",
    minWidth: 58,

    [theme.breakpoints.down("sm")]: {
      minWidth: 44,
      fontSize: 9,
    },
  },

  bulletLabelBackfire: {
    color: "#ff8a8a",
  },

  bulletIcon: {
    width: 16,
    height: 16,
    objectFit: "contain",
    flexShrink: 0,

    [theme.breakpoints.down("sm")]: {
      width: 14,
      height: 14,
    },
  },

  bulletValue: {
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 14,
    fontWeight: 700,
    color: "white",
  },

  bulletsEmpty: {
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 14,
    fontWeight: 700,
    color: "#888",
  },

  emptyState: {
    fontFamily: theme.design.typography.fontFamily,
    fontSize: 16,
    fontWeight: 700,
    color: "#888",
    textAlign: "center",
    padding: "32px 16px",
    backgroundColor: "rgba(0, 0, 0, 0.2)",
    borderRadius: 8,
    marginTop: 16,
    maxWidth: 960,
    width: "100%",
    boxSizing: "border-box",
  },

  loadingBox: {
    display: "flex",
    justifyContent: "center",
    padding: "48px 0",
    width: "100%",
  },
}));

export default styles;
